import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Grid,
  Chip,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CalendarIntegration from '../components/CalendarIntegration';

const BookingConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const appointmentData = location.state?.appointmentData;
  const [clientEmail, setClientEmail] = useState<string>('');

  // Fetch client email if needed
  useEffect(() => {
    if (user && user.email) {
      setClientEmail(user.email);
    } else if (appointmentData && appointmentData.clientId) {
      // Fetch client email from API if not available in auth context
      fetch(`https://salon.rockyroadsolutions.com/api/clients/${appointmentData.clientId}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.email) {
            setClientEmail(data.email);
          }
        })
        .catch(err => console.error('Error fetching client:', err));
    }
  }, [user, appointmentData]);

  if (!appointmentData) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          No appointment data found
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/book-appointment')}
        >
          Return to Booking
        </Button>
      </Box>
    );
  }

  // Format appointment for calendar integration
  const calendarAppointment = {
    id: appointmentData.id || 'temp-id',
    title: `${appointmentData.services.map((s: any) => s.name).join(', ')} at Bella Vista Salon`,
    startTime: new Date(appointmentData.appointmentDate + 'T' + appointmentData.startTime),
    endTime: new Date(new Date(appointmentData.appointmentDate + 'T' + appointmentData.startTime).getTime() + 
      appointmentData.totalDuration * 60000),
    location: 'Bella Vista Salon & Spa, 123 Main Street',
    description: `Your appointment for ${appointmentData.services.map((s: any) => s.name).join(', ')} with ${appointmentData.staff.firstName} ${appointmentData.staff.lastName} at Bella Vista Salon & Spa.`,
    attendees: clientEmail ? [clientEmail] : [] // Add client email as attendee
  };

  // Handle calendar events
  const handleCalendarEvent = (provider: string, action: string, success: boolean) => {
    console.log(`Calendar event: ${provider} ${action} ${success ? 'succeeded' : 'failed'}`);
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      mx: 'auto', 
      py: 4,
      px: 2,
      background: `
        linear-gradient(135deg, rgba(255, 248, 245, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%),
        url('https://images.unsplash.com/photo-1560066984-138dadb4c035?ixlib=rb-4.0.3&auto=format&fit=crop&w=1974&q=80')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      minHeight: '100vh'
    }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success.main, mb: 2 }} />
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Booking Confirmed!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your appointment has been successfully booked. We look forward to seeing you!
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Appointment Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body1">
                <strong>Date:</strong> {new Date(appointmentData.appointmentDate).toLocaleDateString()}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body1">
                <strong>Time:</strong> {appointmentData.startTime}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body1">
                <strong>Staff:</strong> {appointmentData.staff.firstName} {appointmentData.staff.lastName}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="body1">
                <strong>Location:</strong> Bella Vista Salon & Spa, 123 Main Street
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>
          Services
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {appointmentData.services.map((service: any) => (
            <Chip 
              key={service.name}
              label={`${service.name} - $${service.price}`}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1" fontWeight={600}>
            Total Duration:
          </Typography>
          <Typography variant="body1">
            {appointmentData.totalDuration} minutes
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1" fontWeight={600}>
            Subtotal:
          </Typography>
          <Typography variant="body1">
            ${appointmentData.services.reduce((sum: number, service: any) => sum + parseFloat(service.price), 0).toFixed(2)}
          </Typography>
        </Box>
        
        {appointmentData.tipAmount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body1" fontWeight={600}>
              Tip:
            </Typography>
            <Typography variant="body1">
              ${appointmentData.tipAmount.toFixed(2)}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1" fontWeight={600}>
            Total Price:
          </Typography>
          <Typography variant="h6" fontWeight={600} color="primary.main">
            ${(appointmentData.services.reduce((sum: number, service: any) => sum + parseFloat(service.price), 0) + (appointmentData.tipAmount || 0)).toFixed(2)}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Add to Calendar
        </Typography>
        
        {/* Display current email */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Using email: {clientEmail || 'Not set'} 
          <Button 
            size="small" 
            onClick={() => window.open('/set-email-direct.html', '_blank')}
            sx={{ ml: 1, fontSize: '0.7rem' }}
          >
            Change
          </Button>
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <CalendarIntegration 
            appointment={calendarAppointment}
            onCalendarEvent={handleCalendarEvent}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/appointments')}
          >
            View My Appointments
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BookingConfirmation;
